import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  useEffect,
} from "react";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

type PopoverProps = ComponentPropsWithoutRef<typeof PopoverPrimitive.Root>;

function Popover({ onOpenChange, open, ...props }: PopoverProps) {
  // Manual outside click detection for mobile Safari
  // Works around Radix UI's onInteractOutside not firing when popover is inside a modal with stopPropagation
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: Event) => {
      const target = e.target as HTMLElement;
      const popoverContent = target.closest("[data-radix-popover-content]");
      const clickPanel = target.closest('[data-testid*="-side-panel"]');

      // Only handle if click is on panel but not on popover content
      if (!popoverContent && clickPanel && onOpenChange) {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("touchstart", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [open, onOpenChange]);

  return (
    <PopoverPrimitive.Root onOpenChange={onOpenChange} open={open} {...props} />
  );
}
Popover.displayName = PopoverPrimitive.Root.displayName;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(
  (
    { align = "center", className, side = "bottom", sideOffset = 4, ...props },
    ref,
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        className={cnTwMerge(
          "z-modal defaultTransition rounded-default border-none bg-background/card p-4 text-text/primary shadow-popover outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ",
          className,
        )}
        ref={ref}
        side={side}
        sideOffset={sideOffset}
        {...props}
      />
    </PopoverPrimitive.Portal>
  ),
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverContent, PopoverTrigger };
